/**
 * TimelineItem Enhanced Component
 * 
 * Unique Properties:
 * - Event sequence chronological view
 * - Timestamps with timezone support
 * - Audit trail with user tracking
 * - Advanced filtering and grouping
 * - Event hierarchy with parent-child relationships
 * - Performance metrics and analytics
 * - Export capabilities for reporting
 * - Interactive timeline with zoom and navigation
 */

import {
    ActionIcon,
    Alert,
    Badge,
    Button,
    Card,
    Container,
    Divider,
    Grid,
    Group,
    Modal,
    MultiSelect,
    Paper,
    Progress,
    RingProgress,
    SegmentedControl,
    Select,
    Stack,
    Tabs,
    Text,
    TextInput,
    Timeline,
    Title
} from '@mantine/core';
import '@mantine/core/styles.css';
import { notifications } from '@mantine/notifications';
import {
    IconCalendar,
    IconChartBar,
    IconDownload,
    IconFilter,
    IconHistory,
    IconRefresh,
    IconSearch,
    IconSortAscending,
    IconTimeline,
    IconUser,
    IconZoomIn,
    IconZoomOut
} from '@tabler/icons-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { TabulatorFull } from 'tabulator-tables';
import 'tabulator-tables/dist/css/tabulator_midnight.min.css';
import { useTimelineAnalytics } from '../data/hooks/useTimeline';
import { useTimelineEvents } from '../hooks/useWorkflowState';
import WorkflowSearchMenu from './WorkflowSearchMenu';

interface TimelineEvent {
    id: string;
    timestamp: string;
    event_type: string;
    actor: string;
    action: string;
    target: string;
    result: 'success' | 'failure' | 'warning' | 'info';
    duration_ms: number;
    parent_event_id?: string;
    child_events?: string[];
    metadata: Record<string, any>;
    context?: Record<string, any>;
    severity: 'low' | 'medium' | 'high' | 'critical';
}

interface TimelineAnalytics {
    total_events: number;
    error_rate: number;
    peak_hour: string;
    avg_duration: number;
    event_types_breakdown: Record<string, number>;
    actors_activity: Record<string, number>;
}

interface TimelineItemProps {
    instanceId?: string;
    workflowId?: string;
    actorFilter?: string;
    data?: TimelineEvent[];
    mode?: 'timeline' | 'gantt' | 'list' | 'analytics';
    timeRange?: 'hour' | 'day' | 'week' | 'month';
    onEventClick?: (event: TimelineEvent) => void;
}

export const TimelineItemEnhanced: React.FC<TimelineItemProps> = ({
    instanceId,
    workflowId,
    actorFilter,
    data = [],
    mode = 'timeline',
    timeRange = 'day',
    onEventClick
}) => {
    const [activeTab, setActiveTab] = useState('timeline');
    const [selectedEvent, setSelectedEvent] = useState<TimelineEvent | null>(null);
    const [modalOpened, setModalOpened] = useState(false);
    const [groupBy, setGroupBy] = useState<string>('none');
    const [filterActor, setFilterActor] = useState<string[]>([]);
    const [filterType, setFilterType] = useState<string[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [zoomLevel, setZoomLevel] = useState(1);

    const tableRef = useRef<HTMLDivElement>(null);
    const ganttRef = useRef<HTMLDivElement>(null);
    const tabulator = useRef<InstanceType<typeof TabulatorFull> | null>(null);
    const ganttTabulator = useRef<InstanceType<typeof TabulatorFull> | null>(null);

    // Use real data hooks
    const { events, loading, error, refetch } = useTimelineEvents();
    const { analytics, loading: analyticsLoading } = useTimelineAnalytics({
        instanceId,
        workflowId,
        timeRange
    });

    const processedEvents = data.length > 0 ? data : events;

    // Build event hierarchy for tree display
    const buildEventHierarchy = useMemo(() => {
        const eventMap = new Map(processedEvents.map(event => [event.id, { ...event, _children: [] as any[] }]));
        const rootEvents: any[] = [];

        processedEvents.forEach(event => {
            const processedEvent = eventMap.get(event.id);
            if (!processedEvent) return; // Skip if event not found

            if (event.parent_event_id) {
                const parent = eventMap.get(event.parent_event_id);
                if (parent) {
                    parent._children.push(processedEvent);
                }
            } else {
                rootEvents.push(processedEvent);
            }
        });

        return rootEvents;
    }, [processedEvents]);

    // Group events based on selected grouping
    const groupEvents = useMemo(() => {
        let grouped: Record<string, TimelineEvent[]> = {};

        switch (groupBy) {
            case 'actor':
                grouped = processedEvents.reduce((acc, event) => {
                    const key = event.actor;
                    if (!acc[key]) acc[key] = [];
                    acc[key].push(event);
                    return acc;
                }, {} as Record<string, TimelineEvent[]>);
                break;

            case 'type':
                grouped = processedEvents.reduce((acc, event) => {
                    const key = event.event_type;
                    if (!acc[key]) acc[key] = [];
                    acc[key].push(event);
                    return acc;
                }, {} as Record<string, TimelineEvent[]>);
                break;

            case 'hour':
                grouped = processedEvents.reduce((acc, event) => {
                    const date = new Date(event.timestamp);
                    const key = `${date.getHours()}:00`;
                    if (!acc[key]) acc[key] = [];
                    acc[key].push(event);
                    return acc;
                }, {} as Record<string, TimelineEvent[]>);
                break;

            case 'day':
                grouped = processedEvents.reduce((acc, event) => {
                    const date = new Date(event.timestamp);
                    const key = date.toLocaleDateString();
                    if (!acc[key]) acc[key] = [];
                    acc[key].push(event);
                    return acc;
                }, {} as Record<string, TimelineEvent[]>);
                break;

            default:
                grouped = { 'All Events': processedEvents };
        }

        return grouped;
    }, [processedEvents, groupBy]);

    // Filter events based on search and filters
    const filteredEvents = useMemo(() => {
        return processedEvents.filter(event => {
            const matchesActor = filterActor.length === 0 || filterActor.includes(event.actor);
            const matchesType = filterType.length === 0 || filterType.includes(event.event_type);
            const matchesSearch = searchQuery === '' || 
                event.action.toLowerCase().includes(searchQuery.toLowerCase()) ||
                event.target.toLowerCase().includes(searchQuery.toLowerCase()) ||
                event.actor.toLowerCase().includes(searchQuery.toLowerCase());

            return matchesActor && matchesType && matchesSearch;
        });
    }, [processedEvents, filterActor, filterType, searchQuery]);

    // Get unique values for filters
    const uniqueActors = [...new Set(processedEvents.map(e => e.actor))];
    const uniqueTypes = [...new Set(processedEvents.map(e => e.event_type))];

    // Initialize tables
    useEffect(() => {
        if (tableRef.current && !tabulator.current) {
            tabulator.current = new TabulatorFull(tableRef.current, {
                data: buildEventHierarchy,
                layout: 'fitColumns',
                dataTree: true,
                dataTreeStartExpanded: false,
                dataTreeChildField: '_children',
                pagination: 'local',
                paginationSize: 20,
                columns: [
                    {
                        title: 'Timestamp',
                        field: 'timestamp',
                        width: 180,
                        formatter: (cell: { getValue: () => string | number | Date }) => {
                            const raw = cell.getValue();
                            const value = new Date(raw as any);
                            return `<strong>${value.toLocaleString()}</strong>`;
                        },
                        sorter: 'datetime',
                        sortable: true
                    },
                    {
                        title: 'Event Type',
                        field: 'event_type',
                        width: 120,
                        formatter: (cell: { getValue: () => string }) => {
                            const value = cell.getValue();
                            const colors: Record<string, string> = {
                                'state_transition': '#339af0',
                                'signal_received': '#51cf66',
                                'action_executed': '#ffd43b',
                                'error_occurred': '#ff6b6b'
                            };
                            return `<span style="color: ${colors[value] || '#868e96'}; font-weight: bold;">🔄 ${value}</span>`;
                        }
                    },
                    {
                        title: 'ActorDescriptor',
                        field: 'actor',
                        width: 100,
                        formatter: (cell: { getValue: () => string }) => `<span style="color: #495057;">👤 ${cell.getValue()}</span>`
                    },
                    {
                        title: 'Action → Target',
                        formatter: (cell: any) => {
                            const data = cell.getData() as TimelineEvent;
                            return `${data.action} → <code>${data.target}</code>`;
                        },
                        width: 200
                    },
                    {
                        title: 'Result',
                        field: 'result',
                        width: 100,
                        formatter: (cell: { getValue: () => 'success' | 'failure' | 'warning' | 'info' }) => {
                            const value = cell.getValue();
                            const config: Record<string, { color: string; icon: string }> = {
                                success: { color: 'green', icon: '✅' },
                                failure: { color: 'red', icon: '❌' },
                                warning: { color: 'orange', icon: '⚠️' },
                                info: { color: 'blue', icon: 'ℹ️' }
                            };
                            const c = config[value] || config.info;
                            return `<span style="color: ${c?.color};">${c?.icon} ${value}</span>`;
                        }
                    },
                    {
                        title: 'Duration',
                        field: 'duration_ms',
                        width: 100,
                        formatter: (cell: { getValue: () => number }) => {
                            const ms = cell.getValue();
                            if (ms < 1000) return `${ms}ms`;
                            return `${(ms / 1000).toFixed(1)}s`;
                        },
                        sorter: 'number'
                    },
                    {
                        title: 'Severity',
                        field: 'severity',
                        width: 100,
                        formatter: (cell: { getValue: () => 'low' | 'medium' | 'high' | 'critical' }) => {
                            const value = cell.getValue();
                            const colors: Record<string, string> = {
                                low: 'green',
                                medium: 'orange',
                                high: 'red',
                                critical: 'dark'
                            };
                            return `<span style="color: ${colors[value]}; font-weight: bold;">${value.toUpperCase()}</span>`;
                        }
                    },
                    {
                        title: 'Actions',
                        formatter: () => '<button class="view-btn">👁️</button>',
                        width: 80,
                        hozAlign: 'center',
                        cellClick: (e: any, cell: any) => {
                            const eventData = cell.getData() as TimelineEvent;
                            setSelectedEvent(eventData);
                            setModalOpened(true);
                            onEventClick?.(eventData);
                        }
                    }
                ]
            });
        }
    }, [buildEventHierarchy, onEventClick]);

    // Initialize Gantt chart
    useEffect(() => {
        if (ganttRef.current && !ganttTabulator.current) {
            ganttTabulator.current = new TabulatorFull(ganttRef.current, {
                data: filteredEvents.map(event => ({
                    ...event,
                    start: new Date(event.timestamp).getTime(),
                    end: new Date(event.timestamp).getTime() + event.duration_ms
                })),
                layout: 'fitColumns',
                columns: [
                    {
                        title: 'ExecutionResult',
                        field: 'action',
                        width: 150
                    },
                    {
                        title: 'Timeline',
                        formatter: (cell: any) => {
                            const data = cell.getData() as any;
                            const totalDuration = Math.max(...filteredEvents.map(e => e.duration_ms));
                            const widthPercent = (data.duration_ms / totalDuration) * 100;
                            
                            return `
                                <div style="position: relative; width: 100%; height: 25px; background: #f1f3f4; border-radius: 4px; margin: 2px 0;">
                                    <div style="position: absolute; width: ${widthPercent}%; height: 100%; background: ${data.result === 'success' ? '#51cf66' : data.result === 'failure' ? '#ff6b6b' : '#ffd43b'}; border-radius: 4px; display: flex; align-items: center; justify-content: center; color: white; font-size: 10px; font-weight: bold;">
                                        ${data.actor}
                                    </div>
                                </div>
                            `;
                        },
                        width: 300
                    },
                    {
                        title: 'Duration',
                        field: 'duration_ms',
                        formatter: (cell: any) => `${cell.getValue()}ms`,
                        width: 100
                    }
                ]
            });
        }
    }, [filteredEvents]);

    // Update table data when events change
    useEffect(() => {
        if (tabulator.current) {
            tabulator.current.setData(buildEventHierarchy);
        }
        if (ganttTabulator.current) {
            ganttTabulator.current.setData(filteredEvents.map(event => ({
                ...event,
                start: new Date(event.timestamp).getTime(),
                end: new Date(event.timestamp).getTime() + event.duration_ms
            })));
        }
    }, [buildEventHierarchy, filteredEvents]);

    // Event statistics
    const eventStats = useMemo(() => {
        const total = filteredEvents.length;
        const successful = filteredEvents.filter(e => e.result === 'success').length;
        const failed = filteredEvents.filter(e => e.result === 'failure').length;
        const avgDuration = filteredEvents.length > 0 
            ? Math.floor(filteredEvents.reduce((acc, e) => acc + e.duration_ms, 0) / filteredEvents.length)
            : 0;

        return {
            total,
            successful,
            failed,
            successRate: total > 0 ? Math.floor((successful / total) * 100) : 0,
            avgDuration
        };
    }, [filteredEvents]);

    // Export functionality
    const handleExport = (format: string) => {
        if (!tabulator.current) return;

        switch (format) {
            case 'csv':
                tabulator.current.download('csv', 'timeline-events.csv');
                break;
            case 'json':
                tabulator.current.download('json', 'timeline-events.json');
                break;
            case 'pdf':
                tabulator.current.download('pdf', 'timeline-events.pdf');
                break;
        }

        notifications.show({
            title: 'Export Started',
            message: `Exporting timeline events as ${format.toUpperCase()}`,
            color: 'blue'
        });
    };

    // Render timeline view
    const renderTimelineView = () => (
        <Stack>
            <Alert color="blue" icon={<IconTimeline size={16} />}>
                Interactive timeline showing {filteredEvents.length} events
            </Alert>
            
            {Object.entries(groupEvents).map(([groupName, events]) => (
                <Card key={groupName} withBorder>
                    <Group justify="space-between" mb="md">
                        <Title order={5}>{groupName}</Title>
                        <Badge>{events.length} events</Badge>
                    </Group>
                    
                    <Timeline active={events.length - 1} lineWidth={2}>
                        {events.slice(0, 10).map((event, idx) => (
                            <Timeline.Item
                                key={event.id}
                                bullet={
                                    event.result === 'success' ? '✅' :
                                    event.result === 'failure' ? '❌' :
                                    event.result === 'warning' ? '⚠️' : 'ℹ️'
                                }
                                title={event.action}
                                color={
                                    event.result === 'success' ? 'green' :
                                    event.result === 'failure' ? 'red' :
                                    event.result === 'warning' ? 'orange' : 'blue'
                                }
                            >
                                <Group gap="xs" mb={4}>
                                    <Badge size="xs" color="gray">{event.event_type}</Badge>
                                    <Badge size="xs" color="blue">{event.actor}</Badge>
                                    <Text size="xs" c="dimmed">{event.duration_ms}ms</Text>
                                </Group>
                                <Text size="sm" c="dimmed">
                                    Target: <code>{event.target}</code>
                                </Text>
                                <Text size="xs" mt={4}>
                                    {new Date(event.timestamp).toLocaleString()}
                                </Text>
                            </Timeline.Item>
                        ))}
                        {events.length > 10 && (
                            <Timeline.Item
                                bullet="..."
                                title={`${events.length - 10} more events`}
                                lineVariant="dashed"
                            />
                        )}
                    </Timeline>
                </Card>
            ))}
        </Stack>
    );

    // Render analytics view
    const renderAnalyticsView = () => (
        <Grid>
            <Grid.Col span={8}>
                <Stack>
                    <Card withBorder>
                        <Title order={5} mb="md">Event Statistics</Title>
                        <Grid>
                            <Grid.Col span={3}>
                                <div style={{ textAlign: 'center' }}>
                                    <Text size="xl" fw={700} c="blue">{eventStats.total}</Text>
                                    <Text size="sm" c="dimmed">Total Events</Text>
                                </div>
                            </Grid.Col>
                            <Grid.Col span={3}>
                                <div style={{ textAlign: 'center' }}>
                                    <Text size="xl" fw={700} c="green">{eventStats.successful}</Text>
                                    <Text size="sm" c="dimmed">Successful</Text>
                                </div>
                            </Grid.Col>
                            <Grid.Col span={3}>
                                <div style={{ textAlign: 'center' }}>
                                    <Text size="xl" fw={700} c="red">{eventStats.failed}</Text>
                                    <Text size="sm" c="dimmed">Failed</Text>
                                </div>
                            </Grid.Col>
                            <Grid.Col span={3}>
                                <div style={{ textAlign: 'center' }}>
                                    <Text size="xl" fw={700}>{eventStats.avgDuration}ms</Text>
                                    <Text size="sm" c="dimmed">Avg Duration</Text>
                                </div>
                            </Grid.Col>
                        </Grid>
                        <Progress value={eventStats.successRate} color="green" mt="md" />
                        <Text size="sm" ta="center" mt={4}>
                            Success Rate: {eventStats.successRate}%
                        </Text>
                    </Card>

                    <Card withBorder>
                        <Title order={5} mb="md">Event Types Distribution</Title>
                        {uniqueTypes.map(type => {
                            const count = filteredEvents.filter(e => e.event_type === type).length;
                            const percentage = (count / filteredEvents.length) * 100;
                            
                            return (
                                <Group key={type} justify="space-between" mb="xs">
                                    <Text size="sm">{type}</Text>
                                    <Group gap="xs">
                                        <Text size="sm" fw={500}>{count}</Text>
                                        <div style={{ width: 100 }}>
                                            <Progress value={percentage} size="sm" />
                                        </div>
                                        <Text size="xs" c="dimmed">{percentage.toFixed(1)}%</Text>
                                    </Group>
                                </Group>
                            );
                        })}
                    </Card>
                </Stack>
            </Grid.Col>

            <Grid.Col span={4}>
                <Card withBorder>
                    <Group justify="space-between" mb="md">
                        <Title order={5}>Success Rate</Title>
                        <Text size="xs" c="dimmed">Last {timeRange}</Text>
                    </Group>
                    <RingProgress
                        size={180}
                        thickness={12}
                        sections={[
                            { value: eventStats.successRate, color: 'green' },
                            { value: 100 - eventStats.successRate, color: 'red' }
                        ]}
                        label={
                            <Text size="lg" ta="center" fw={700}>
                                {eventStats.successRate}%
                            </Text>
                        }
                    />
                </Card>

                <Card withBorder mt="md">
                    <Title order={5} mb="md">Top Actors</Title>
                    {uniqueActors.slice(0, 5).map(actor => {
                        const count = filteredEvents.filter(e => e.actor === actor).length;
                        return (
                            <Group key={actor} justify="space-between" mb="xs">
                                <Text size="sm">👤 {actor}</Text>
                                <Badge>{count}</Badge>
                            </Group>
                        );
                    })}
                </Card>
            </Grid.Col>
        </Grid>
    );

    // Handle loading and error states
    if (loading) {
        return (
            <Container size="xl">
                <Paper shadow="sm" p="lg">
                    <Stack ta="center" gap="md">
                        <Progress value={30} animated size="lg" style={{ width: '100%' }} />
                        <Text>Loading timeline events...</Text>
                    </Stack>
                </Paper>
            </Container>
        );
    }

    if (error) {
        return (
            <Container size="xl">
                <Paper shadow="sm" p="lg">
                    <Alert color="red" title="Error Loading Timeline" icon={<IconHistory />}>
                        <Text>{error.message}</Text>
                        <Button mt="md" variant="outline" onClick={() => refetch()} leftSection={<IconRefresh size={16} />}>
                            Retry
                        </Button>
                    </Alert>
                </Paper>
            </Container>
        );
    }

    return (
        <Container size="xl">
            <Paper shadow="sm" p="lg">
                <Group justify="space-between" mb="lg">
                    <div>
                        <Title order={2}>Timeline Events</Title>
                        <Text c="dimmed">Chronological view of workflow events and activities</Text>
                    </div>
                    <Group>
                        <RingProgress
                            size={60}
                            thickness={6}
                            sections={[{ value: eventStats.successRate, color: 'green' }]}
                            label={
                                <Text size="xs" ta="center">
                                    {eventStats.successRate}%
                                </Text>
                            }
                        />
                        <Button.Group>
                            <Button
                                leftSection={<IconRefresh size={16} />}
                                variant="default"
                                onClick={() => refetch()}
                            >
                                Refresh
                            </Button>
                            <Button
                                leftSection={<IconDownload size={16} />}
                                variant="default"
                                onClick={() => handleExport('csv')}
                            >
                                Export
                            </Button>
                        </Button.Group>
                    </Group>
                </Group>

                {/* Filters */}
                <Card withBorder mb="lg">
                    <Grid>
                        <Grid.Col span={3}>
                            <TextInput
                                placeholder="Search events..."
                                leftSection={<IconSearch size={16} />}
                                value={searchQuery}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.currentTarget.value)}
                            />
                        </Grid.Col>
                        <Grid.Col span={2}>
                            <MultiSelect
                                placeholder="Filter by actor"
                                data={uniqueActors}
                                value={filterActor}
                                onChange={setFilterActor}
                                leftSection={<IconUser size={16} />}
                            />
                        </Grid.Col>
                        <Grid.Col span={2}>
                            <MultiSelect
                                placeholder="Filter by type"
                                data={uniqueTypes}
                                value={filterType}
                                onChange={setFilterType}
                                leftSection={<IconFilter size={16} />}
                            />
                        </Grid.Col>
                        <Grid.Col span={2}>
                            <Select
                                placeholder="Group by"
                                data={[
                                    { value: 'none', label: 'No grouping' },
                                    { value: 'actor', label: 'By actor' },
                                    { value: 'type', label: 'By type' },
                                    { value: 'hour', label: 'By hour' },
                                    { value: 'day', label: 'By day' }
                                ]}
                                value={groupBy}
                                onChange={(value: string | null) => setGroupBy(value ?? 'none')}
                                leftSection={<IconSortAscending size={16} />}
                            />
                        </Grid.Col>
                        <Grid.Col span={3}>
                            <SegmentedControl
                                value={timeRange}
                                data={[
                                    { label: 'Hour', value: 'hour' },
                                    { label: 'Day', value: 'day' },
                                    { label: 'Week', value: 'week' },
                                    { label: 'Month', value: 'month' }
                                ]}
                                fullWidth
                            />
                        </Grid.Col>
                    </Grid>
                </Card>

                <Tabs value={activeTab} onChange={(value: string | null) => setActiveTab(value ?? 'timeline')}>
                    <Tabs.List>
                        <Tabs.Tab value="timeline" leftSection={<IconTimeline size={16} />}>
                            Timeline ({filteredEvents.length})
                        </Tabs.Tab>
                        <Tabs.Tab value="table" leftSection={<IconHistory size={16} />}>
                            Table View
                        </Tabs.Tab>
                        <Tabs.Tab value="gantt" leftSection={<IconCalendar size={16} />}>
                            Gantt Chart
                        </Tabs.Tab>
                        <Tabs.Tab value="analytics" leftSection={<IconChartBar size={16} />}>
                            Analytics
                        </Tabs.Tab>
                    </Tabs.List>

                    <Tabs.Panel value="timeline">
                        <div style={{ marginTop: '1rem' }}>
                            {renderTimelineView()}
                        </div>
                    </Tabs.Panel>

                    <Tabs.Panel value="table">
                        <div ref={tableRef} style={{ marginTop: '1rem' }} />
                    </Tabs.Panel>

                    <Tabs.Panel value="gantt">
                        <Stack mt="md">
                            <Group justify="space-between">
                                <Text size="sm" c="dimmed">
                                    Gantt view showing event duration and timeline
                                </Text>
                                <Group>
                                    <ActionIcon
                                        variant="default"
                                        onClick={() => setZoomLevel(Math.min(zoomLevel * 1.2, 3))}
                                    >
                                        <IconZoomIn size={16} />
                                    </ActionIcon>
                                    <ActionIcon
                                        variant="default"
                                        onClick={() => setZoomLevel(Math.max(zoomLevel * 0.8, 0.5))}
                                    >
                                        <IconZoomOut size={16} />
                                    </ActionIcon>
                                </Group>
                            </Group>
                            <div ref={ganttRef} style={{ transform: `scale(${zoomLevel})`, transformOrigin: 'top left' }} />
                        </Stack>
                    </Tabs.Panel>

                    <Tabs.Panel value="analytics">
                        <div style={{ marginTop: '1rem' }}>
                            {analyticsLoading ? (
                                <Stack ta="center" gap="md">
                                    <Progress value={50} animated size="sm" style={{ width: '100%' }} />
                                    <Text size="sm" c="dimmed">Loading analytics...</Text>
                                </Stack>
                            ) : (
                                renderAnalyticsView()
                            )}
                        </div>
                    </Tabs.Panel>
                </Tabs>
            </Paper>

            {/* Event Details Modal */}
            <Modal
                opened={modalOpened}
                onClose={() => setModalOpened(false)}
                title="Event Details"
                size="lg"
            >
                {selectedEvent && (
                    <Stack>
                        <Grid>
                            <Grid.Col span={6}>
                                <Text size="sm" c="dimmed">Event ID</Text>
                                <code>{selectedEvent.id}</code>
                            </Grid.Col>
                            <Grid.Col span={6}>
                                <Text size="sm" c="dimmed">Type</Text>
                                <Badge>{selectedEvent.event_type}</Badge>
                            </Grid.Col>
                            <Grid.Col span={6}>
                                <Text size="sm" c="dimmed">Actor</Text>
                                <Text>👤 {selectedEvent.actor}</Text>
                            </Grid.Col>
                            <Grid.Col span={6}>
                                <Text size="sm" c="dimmed">Result</Text>
                                <Badge color={
                                    selectedEvent.result === 'success' ? 'green' :
                                    selectedEvent.result === 'failure' ? 'red' :
                                    selectedEvent.result === 'warning' ? 'orange' : 'blue'
                                }>
                                    {selectedEvent.result}
                                </Badge>
                            </Grid.Col>
                        </Grid>

                        <Divider />

                        <div>
                            <Text size="sm" c="dimmed">Action</Text>
                            <Text fw={500}>{selectedEvent.action}</Text>
                        </div>

                        <div>
                            <Text size="sm" c="dimmed">Target</Text>
                            <code>{selectedEvent.target}</code>
                        </div>

                        <div>
                            <Text size="sm" c="dimmed">Timestamp</Text>
                            <Text>{new Date(selectedEvent.timestamp).toLocaleString()}</Text>
                        </div>

                        <div>
                            <Text size="sm" c="dimmed">Duration</Text>
                            <Text>{selectedEvent.duration_ms}ms</Text>
                        </div>

                        {selectedEvent.metadata && Object.keys(selectedEvent.metadata).length > 0 && (
                            <div>
                                <Text size="sm" c="dimmed">Metadata</Text>
                                <pre style={{ 
                                    background: '#f8f9fa', 
                                    padding: '8px', 
                                    borderRadius: '4px', 
                                    fontSize: '12px',
                                    overflow: 'auto',
                                    maxHeight: '200px'
                                }}>
                                    {JSON.stringify(selectedEvent.metadata, null, 2)}
                                </pre>
                            </div>
                        )}
                    </Stack>
                )}
            </Modal>

            {/* Integrated Search Menu */}
            <WorkflowSearchMenu
                workflowType="TimelineItem"
                workflowIcon="⏱️"
                workflowColor="cyan"
                position="bottom"
                height="350px"
                onItemSelect={(item) => {
                    console.log('Selected timeline item:', item);
                }}
                onSearch={(query, filters) => {
                    console.log('Timeline search query:', query, 'Filters:', filters);
                }}
            />
        </Container>
    );
};

export default TimelineItemEnhanced;